import { createSlice } from "@reduxjs/toolkit";
import { RESET } from "../types";
import { createTransform } from "redux-persist";

const initialState = {
  product: null,
  editingProduct: null,
  cartProducts: [],
  categoryFilter: undefined,
  productTitleFilter: undefined,
  orderType: null,
  address: null,
  showAddressForm: null,
  showCustomerForm: null,
  customer: null,
  total: 0,
  totalToPay: 0,
  itemsTotal: 0,
  deliveryTax: 0,
  discounts: [],
  discountsValue: 0,
  applyServiceFee: false,
  serviceFee: 0,
  serviceFeeAmount: 0,
  paymentMethods: [],
  showPaymentForm: false,
  newOrderTable: undefined,
  sendingNewOrder: undefined,
  newOrderId: undefined,
  expandGrids: false,
  showCartDrawer: false,
};

export const newOrderTransform = createTransform(
  // Serialize state before persisting
  (inboundState) => ({
    customer: inboundState.customer,
    cartProducts: inboundState.cartProducts,
    orderType: inboundState.orderType,
    total: inboundState.total,
    itemsTotal: inboundState.itemsTotal,
    expandGrids: inboundState.expandGrids,
  }),
  // Deserialize state when rehydrating
  (outboundState) => ({
    ...outboundState,
  }),
  { whitelist: ["newOrder"] }
);

const newOrderSlice = createSlice({
  name: "newOrder",
  initialState,
  reducers: {
    setItem: (state, action) => {
      if (action.payload) {
        state.product = {
          ...action.payload,
          total_price: action.payload?.price || undefined,
        };
      } else {
        state.product = null;
      }
    },
    setOrderType: (state, action) => {
      state.orderType = action.payload;
    },
    setAddress: (state, action) => {
      state.address = action.payload;
      state.showAddressForm = false;
    },
    setShowAddressForm: (state, action) => {
      state.showAddressForm = action.payload;
    },
    setCustomer: (state, action) => {
      state.customer = action.payload;
      state.showCustomerForm = false;
    },
    setShowCustomerForm: (state, action) => {
      state.showCustomerForm = action.payload;
    },
    setShowPaymentForm: (state, action) => {
      state.showPaymentForm = action.payload;
    },
    setShowCartDrawer: (state, action) => {
      state.showCartDrawer = action.payload;
    },
    updateOrderValues: (state, action) => {
      const body = action.payload;

      state.total = body.total;
      state.totalToPay = body.totalToPay;
      state.itemsTotal = body.itemsTotal;
      state.discountsValue = body.discountsValue;
      state.serviceFeeAmount = body.serviceFeeAmount;
    },
    setNewOrderId: (state, action) => {
      state.newOrderId = action.payload;
    },
    setServiceFee: (state, action) => {
      state.serviceFee = action.payload;
    },
    setApplyServiceFee: (state, action) => {
      state.applyServiceFee = action.payload;

      if (action.payload === true) {
        state.serviceFeeAmount = (state.serviceFee / 100) * state.totalToPay;
        state.totalToPay = state.totalToPay + state.serviceFeeAmount;
      }
    },
    addDiscount: (state, action) => {
      state.discounts.push(action.payload);
    },
    removeDiscount: (state, action) => {
      state.discounts = state.discounts.filter(
        (discount) => discount.id !== action.payload
      );
    },
    setPaymentMethods: (state, action) => {
      state.paymentMethods = action.payload;
    },
    addPaymentMethod: (state, action) => {
      state.paymentMethods.push(action.payload);
    },
    removePaymentMethod: (state, action) => {
      state.paymentMethods = state.paymentMethods.filter(
        (paymentMethod) => paymentMethod.id !== action.payload
      );
    },
    setExpandGrids: (state, action) => {
      state.expandGrids = action.payload;
    },
    setCategoryFilter: (state, action) => {
      state.categoryFilter = action.payload;
    },
    setProductTitleFilter: (state, action) => {
      state.productTitleFilter = action.payload;
    },
    addProductToCartProducts: (state, action) => {
      const newProduct = {
        ...state.product,
        quantity: state.product.quantity || 1,
        un_price: state.product.price,
      };
      const existingProductIndex = state.cartProducts.findIndex(
        (p) => p.product_identifier === newProduct.product_identifier
      );

      if (existingProductIndex !== -1) {
        state.cartProducts[existingProductIndex] = newProduct;
      } else {
        state.cartProducts.push(newProduct);
      }

      state.product = null;
    },
    removeProductFromCart: (state, action) => {
      state.cartProducts = state.cartProducts.filter(
        (p) => p.product_identifier !== action.payload
      );
    },
    increaseProductQuantity: (state, action) => {
      const productIndex = state.cartProducts.findIndex(
        (p) => p.product_identifier === action.payload
      );

      if (productIndex !== -1) {
        state.cartProducts[productIndex].quantity += 1;
      }
    },
    decreaseProductQuantity: (state, action) => {
      const productIndex = state.cartProducts.findIndex(
        (p) => p.product_identifier === action.payload
      );

      if (productIndex !== -1) {
        if (state.cartProducts[productIndex].quantity > 1) {
          state.cartProducts[productIndex].quantity -= 1;
        } else {
          // Remove product if quantity reaches 0
          state.cartProducts = state.cartProducts.filter(
            (p) => p.product_identifier !== action.payload
          );
        }
      }
    },
    // NEW ACTION: Update cart product quantity to a specific value
    updateCartProductQuantity: (state, action) => {
      const { productIdentifier, quantity } = action.payload;
      const productIndex = state.cartProducts.findIndex(
        (p) => p.product_identifier === productIdentifier
      );

      if (productIndex !== -1) {
        if (quantity > 0) {
          state.cartProducts[productIndex].quantity = quantity;
        } else {
          // Remove product if quantity is 0 or less
          state.cartProducts = state.cartProducts.filter(
            (p) => p.product_identifier !== productIdentifier
          );
        }
      }
    },
    addComplement: (state, action) => {
      const addedComplement = action.payload;

      // Ensure state.product exists
      if (!state.product) {
        const complementsPrice = addedComplement.price; // Start with the added complement price
        state.product = {
          product_identifier: `product${state.cartProducts.length}`,
          quantity: 1,
          complements: [{ ...addedComplement, quantity: 1 }],
          complementsPrice, // Track complement price separately
          total_price: complementsPrice + state.product.price, // Initialize total price
        };
        return;
      }

      // Ensure complements array exists
      if (!state.product.complements) {
        state.product.complements = [];
      }

      // Find existing complement
      const complementIndex = state.product.complements.findIndex(
        (c) => c.id === addedComplement.id
      );

      if (complementIndex !== -1) {
        // If complement exists, increase its quantity
        state.product.complements[complementIndex].quantity += 1;
      } else {
        // Otherwise, add new complement
        state.product.complements.push({ ...addedComplement, quantity: 1 });
      }

      // Calculate new complements price
      state.product.complementsPrice = state.product.complements.reduce(
        (total, complement) => total + complement.price * complement.quantity,
        0
      );

      // Update total price
      state.product.total_price =
        parseFloat(state.product.complementsPrice) +
        parseFloat(state.product.price);
    },
    decreaseProductComplementQuantity: (state, action) => {
      const { id } = action.payload;

      // Ensure the product exists
      const product = state.product;

      if (!product || !product.complements) {
        return; // No product or complements to modify
      }

      // Find the complement
      const complementIndex = product.complements.findIndex(
        (complement) => complement.id === id
      );

      if (complementIndex === -1) {
        return; // Complement not found
      }

      // Decrease the quantity
      product.complements[complementIndex].quantity -= 1;

      // If quantity goes below 1, remove the complement
      if (product.complements[complementIndex].quantity < 1) {
        product.complements.splice(complementIndex, 1);
      }

      // Recalculate complementsPrice
      product.complementsPrice = product.complements.reduce(
        (total, complement) => total + complement.price * complement.quantity,
        0
      );

      // Update total_price
      product.total_price =
        parseFloat(product.complementsPrice) + parseFloat(product.price);
    },
    deleteProductComplement: (state, action) => {
      const { id } = action.payload;

      // Ensure the product exists
      if (!state.product || !state.product.complements) {
        return;
      }

      // Remove the complement by filtering out the matching id
      state.product.complements = state.product.complements.filter(
        (complement) => complement.id !== id
      );

      // Recalculate complementsPrice
      state.product.complementsPrice = state.product.complements.reduce(
        (total, complement) => total + complement.price * complement.quantity,
        0
      );

      // Update total_price
      state.product.total_price =
        parseFloat(state.product.complementsPrice) +
        parseFloat(state.product.price);
    },
    setCartProducts: (state, action) => {
      state.cartProducts = action.payload;
    },
    resetNewOrderSlice: (state, action) => {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(RESET, () => initialState);
  },
});

export const selectProductByIdentifier = (state, productIdentifier) =>
  state.newOrder.cartProducts.find(
    (product) => product.product_identifier === productIdentifier
  );

export const {
  setItem,
  setOrderType,
  setAddress,
  setShowAddressForm,
  setCustomer,
  setShowCustomerForm,
  setShowPaymentForm,
  setShowCartDrawer,
  updateOrderValues,
  setNewOrderId,
  setServiceFee,
  setApplyServiceFee,
  addDiscount,
  removeDiscount,
  setPaymentMethods,
  addPaymentMethod,
  removePaymentMethod,
  setExpandGrids,
  setCategoryFilter,
  setProductTitleFilter,
  addProductToCartProducts,
  removeProductFromCart,
  increaseProductQuantity,
  decreaseProductQuantity,
  updateCartProductQuantity, // NEW ACTION EXPORT
  addComplement,
  deleteProductComplement,
  decreaseProductComplementQuantity,
  setCartProducts,
  resetNewOrderSlice,
} = newOrderSlice.actions;
export default newOrderSlice.reducer;
